import React from 'react'
import type { IChartApi, UTCTimestamp } from 'lightweight-charts'
import type { OpportunitySignals } from '@/modules/signals/services/signalsService'
import type { SignalsOverlayLevels } from '@/modules/signals/utils/signalsOverlayLevels'
import type { TF } from '@/modules/graph-view/types'
import { tfToSeconds } from '@/modules/graph-view/utils/normalize'

type LevelKind = 'entry' | 'target' | 'stop' | 'mid'

type LevelDef = {
  key: string
  label: string
  value: number
  kind: LevelKind
}

type Props = {
  chartRef: React.RefObject<IChartApi | null>
  candles: Array<{ time: number | UTCTimestamp }>
  timeframe: TF
  opportunity: any | null
  signals: OpportunitySignals | null
  overlayLevels?: SignalsOverlayLevels
  enabled: boolean
}

function parseIsoToSeconds(iso?: string | null): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.floor(t / 1000)
}

function buildLevels(signals: OpportunitySignals | null): LevelDef[] {
  if (!signals) return []

  const {
    entryMin,
    entryMax,
    entryMid: rawEntryMid,
    tp1,
    tp2,
    tp3,
    stopPrice,
    stopAggressive,
    stopConservative,
  } = signals

  const levels: LevelDef[] = []

  const baseEntryMid =
    rawEntryMid ??
    (entryMin != null && entryMax != null
      ? (entryMin + entryMax) / 2
      : null)

  // Entradas: se tivermos faixa (min/max), desenhamos Zona Técnica inicial / Zona Técnica final; senÃ£o, usamos o mid ou um Ãºnico valor.
  if (entryMin != null && entryMax != null) {
    const same = Math.abs(entryMin - entryMax) < 1e-12
    levels.push({
      key: 'entry1',
      label: same ? 'Entrada' : 'Zona Técnica inicial',
      value: entryMin,
      kind: 'entry',
    })
    if (!same) {
      levels.push({
        key: 'entry2',
        label: 'Zona Técnica final',
        value: entryMax,
        kind: 'entry',
      })
    }
  } else {
    const v = baseEntryMid ?? entryMin ?? entryMax
    if (v != null) {
      levels.push({
        key: 'entry',
        label: 'Zona Técnica',
        value: v,
        kind: 'entry',
      })
    }
  }

  if (baseEntryMid != null) {
    levels.push({
      key: 'entryMid',
      label: 'Zona de equilíbrio',
      value: baseEntryMid,
      kind: 'mid',
    })
  }

  // Alvos
  if (tp1 != null) {
    levels.push({ key: 'tp1', label: 'Projeção 1', value: tp1, kind: 'target' })
  }
  if (tp2 != null) {
    levels.push({ key: 'tp2', label: 'Projeção 2', value: tp2, kind: 'target' })
  }
  if (tp3 != null) {
    levels.push({ key: 'tp3', label: 'Projeção 3', value: tp3, kind: 'target' })
  }

  // Stop efetivo (prioriza agressivo, depois conservador, depois base)
  const effectiveStop =
    stopAggressive != null
      ? stopAggressive
      : stopConservative != null
      ? stopConservative
      : stopPrice ?? null

  if (effectiveStop != null) {
    levels.push({
      key: 'stop',
      label: 'Invalidação',
      value: effectiveStop,
      kind: 'stop',
    })
  }

  return levels
}

function applyOverlayLevelsVisibility(levels: LevelDef[], cfg?: SignalsOverlayLevels): LevelDef[] {
  if (!cfg) return levels

  const showEntry = cfg.entryInitial || cfg.entryFinal

  return levels.filter((lv) => {
    if (lv.kind === 'target') return cfg.targets
    if (lv.kind === 'stop') return cfg.stop
    if (lv.kind === 'mid' || lv.key === 'entryMid') return cfg.entryMid
    if (lv.key === 'entry1') return cfg.entryInitial
    if (lv.key === 'entry2') return cfg.entryFinal
    if (lv.key === 'entry') return showEntry
    if (lv.kind === 'entry') return showEntry
    return true
  })
}

function levelColor(kind: LevelKind): string {
  switch (kind) {
    case 'mid':
      return '#FFFFFF' // preÃ§o mÃ©dio em branco
    case 'entry':
      return '#FFD600' // amarelo (coerente com EMA 12 / zona de entrada)
    case 'target':
      return '#22c55e' // verde (alvos)
    case 'stop':
      return '#ef4444' // vermelho (stop)
    default:
      return '#9ca3af'
  }
}

type SeriesHandle = {
  series: any
  priceLine?: any
}

export default function OpportunitySignalsOverlay({
  chartRef,
  candles,
  timeframe,
  opportunity,
  signals,
  overlayLevels,
  enabled,
}: Props) {
  const seriesRef = React.useRef<SeriesHandle[]>([])

  React.useEffect(() => {
    const chartApi = chartRef.current

    const cleanup = () => {
      try {
        for (const h of seriesRef.current) {
          try {
            if (h.priceLine && h.series?.removePriceLine) {
              h.series.removePriceLine(h.priceLine)
            }
          } catch {}
          try {
            if (chartApi && h.series && chartApi.removeSeries) {
              chartApi.removeSeries(h.series)
            } else {
              h.series?.remove?.()
            }
          } catch {}
        }
      } catch {}
      seriesRef.current = []
    }

    if (!chartApi || !enabled || !signals || !candles || candles.length === 0) {
      cleanup()
      return () => cleanup()
    }

    cleanup()

    try {
      const levels = applyOverlayLevelsVisibility(buildLevels(signals), overlayLevels)
      if (levels.length === 0) {
        return () => cleanup()
      }

      const times: number[] = []
      for (const c of candles as any[]) {
        const t = Number(c?.time)
        if (Number.isFinite(t)) times.push(t)
      }
      if (times.length === 0) {
        return () => cleanup()
      }
      times.sort((a, b) => a - b)
      const firstT = times[0]
      const lastT = times[times.length - 1]
      if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || firstT >= lastT) {
        return () => cleanup()
      }

      const publishedIso =
        (opportunity as any)?.published_at ?? (opportunity as any)?.created_at ?? null
      const publishedSec = parseIsoToSeconds(publishedIso)
      if (!publishedSec) {
        return () => cleanup()
      }

      // Linha horizontal deve partir do candle mais prÃ³ximo da data/horÃ¡rio de confirmaÃ§Ã£o:
      // - Se published_at < primeiro candle carregado: usa o primeiro candle (firstT).
      // - Se published_at > Ãºltimo candle carregado: ancora no Ãºltimo candle (lastT).
      // - Caso contrÃ¡rio: first candle >= published_at.
      let startTime = firstT
      if (publishedSec <= firstT) {
        startTime = firstT
      } else if (publishedSec >= lastT) {
        startTime = lastT
      } else {
        for (const t of times) {
          if (t >= publishedSec) {
            startTime = t
            break
          }
        }
      }
      if (startTime > lastT) {
        return () => cleanup()
      }

      // ExtensÃ£o atÃ© prÃ³ximo da rÃ©gua do eixo Y (direita): usamos o mesmo offset em barras do grÃ¡fico V1.
      const offsetBars = (() => {
        switch (timeframe) {
          case 'W': return 3
          case 'M': return 1
          default: return 12
        }
      })()
      const offsetSec = tfToSeconds(timeframe) * offsetBars
      const endTime = lastT + offsetSec

      for (const lv of levels) {
        try {
          const color = levelColor(lv.kind)
          const series = chartApi.addLineSeries({
            color,
            lineWidth: 1,
            lineStyle: 2,
            lastValueVisible: false,
            priceLineVisible: false,
          } as any)

          try {
            series.setData([
              { time: startTime as UTCTimestamp, value: lv.value },
              { time: endTime as UTCTimestamp, value: lv.value },
            ])
          } catch {}

          let pl: any
          try {
            pl = series.createPriceLine({
              price: lv.value,
              color,
              lineStyle: 2,
              lineWidth: 1,
              axisLabelVisible: true,
              title: lv.label,
            } as any)
          } catch {}

          seriesRef.current.push({ series, priceLine: pl })
        } catch {}
      }
    } catch {
      // Em caso de falha silenciosa, apenas limpamos no cleanup.
    }

    return () => cleanup()
  }, [chartRef, candles, timeframe, opportunity, signals, overlayLevels, enabled])

  return null
}
